'use client';

import React, { useEffect, useState } from 'react';
import ReactPaginate from 'react-paginate';
import styles from '../product-questions.module.css';

interface QuestionsPaginationProps {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  locale?: {
    PAGINATION_PREV?: string;
    PAGINATION_NEXT?: string;
  };
}

export const QuestionsPagination: React.FC<QuestionsPaginationProps> = ({
  totalPages,
  currentPage,
  onPageChange,
  locale,
}) => {
  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  // Настройки для мобильных устройств (реактивно, учитываем изменение ширины)
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mql = window.matchMedia('(max-width: 480px)');
    const handleChange = () => setIsMobile(mql.matches);
    handleChange();
    mql.addEventListener('change', handleChange);
    return () => {
      mql.removeEventListener('change', handleChange);
    };
  }, []);

  // Для мобильных - максимум 3 кнопки
  const getPageRangeDisplayed = () => {
    if (!isMobile) return 3;
    
    if (totalPages <= 4) {
      return totalPages;
    }
    
    // Для первой и последней страницы - показываем только активную
    if (currentPage === 1 || currentPage === totalPages) {
      return 0; // Только активная + крайние через marginPagesDisplayed
    }
    
    // Для второй и предпоследней страницы - показываем 3 подряд
    if (currentPage === 2 || currentPage === totalPages - 1) {
      return 2; // Показываем 3 страницы подряд
    }
    
    // Для всех остальных средних страниц - показываем активную с соседними
    return 2; // Показываем 3 страницы (активная-1, активная, активная+1)
  };

  const getMarginPagesDisplayed = () => {
    if (!isMobile) return 1;
    
    if (totalPages <= 4) {
      return 0;
    }
    
    // Показываем крайние только для первой и последней страниц
    if (currentPage === 1 || currentPage === totalPages) {
      return 1;
    }
    
    // Для всех остальных страниц НЕ показываем крайние
    return 0;
  };

  // Добавляем кастомную логику для отключения breakLabel
  const getBreakLabel = () => {
    if (!isMobile) return "...";
    
    if (totalPages <= 4) return "...";
    
    // Показываем многоточие только для первой и последней страниц
    if (currentPage === 1 || currentPage === totalPages) {
      return "...";
    }
    
    // Для всех остальных страниц убираем многоточие
    return "";
  };

  return (
    <div className={styles.paginationContainer}>
      <div className={styles.paginationWrapper}>
        <ReactPaginate
          pageCount={totalPages}
          marginPagesDisplayed={getMarginPagesDisplayed()}
          pageRangeDisplayed={getPageRangeDisplayed()}
          forcePage={currentPage - 1}
          previousLabel={!isMobile ? (locale?.PAGINATION_PREV || 'Назад') : ''}
          nextLabel={!isMobile ? (locale?.PAGINATION_NEXT || 'Вперед') : ''}
          containerClassName={styles.pagination}
          pageLinkClassName={styles.pageNum}
          activeClassName={styles.active}
          disabledClassName={styles.disabled}
          breakClassName={styles.break}
          nextClassName={`${styles.navigate} ${styles.next} ${isMobile ? styles.hidden : ''}`}
          previousClassName={`${styles.navigate} ${styles.previous} ${isMobile ? styles.hidden : ''}`}
          onPageChange={({ selected }) => onPageChange(selected + 1)}
          breakLabel={getBreakLabel()}
        />
      </div>
      
      <div className={styles.navigationButtons}>
        <button
          type="button"
          className={`${styles.navButton} ${styles.navButtonPrev} ${currentPage === 1 ? styles.navButtonDisabled : ''}`}
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          aria-label="Предыдущая страница"
        />
        <button
          type="button"
          className={`${styles.navButton} ${styles.navButtonNext} ${currentPage === totalPages ? styles.navButtonDisabled : ''}`}
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          aria-label="Следующая страница"
        />
      </div>
    </div>
  );
}; 